import get from "lodash/get";
import React, { useContext, useEffect, useMemo, useState } from "react";

import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form/dist/index.ie11";

import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";

import {
  useAddNotificationMutation,
  useGetAllPlacesQuery,
  useGetDealerPanelQuery,
  useGetPlanReportQuery,
  useGetProjectDocumentationDealerQuery,
  useGetZdDealerOneQuery,
  useSetChangeStatusZdGlobalForDealerMutation,
  ZdGlobalInfoForDealerStatus,
} from "~/schemas/generated/graphql";
import { useDealerActions } from "~/app/Context/DealerContext";
import {
  GetCurrentDealerContext,
  GetCurrentDealerContextPrpos,
} from "~/pages/IvideonDealerRegistry/getCurrentDealer";

import Button from "~/shared/ui/Fields/Button";
import DealerInfoPanel from "~/components/DealerInfoPanel";
import Back from "~/components/Icons/arrow-prev2";
import Select from "~/shared/ui/Fields/Select";

import TableReducerZD from "~/pages/ZD/TableReducerZD";

import { NOTIFICATIONS } from "~/constans/notifications";
import {
  ZD_CHECK_PERMISSIONS,
  ZD_SELECT_BY_PERMISSIONS,
} from "~/pages/Editor/EditorServiceAuthorization/options";

import { SUB_OPTIONS } from "./TableReducerZD/options";

import s from "~/pages/Dealers/Dealers.less";
import clsx from "clsx";
import { TRole } from "~/types/Routes";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useUser } from "~/shared/context/UserContext";
import { useZdRequests } from "~/shared/hooks/useZDRequests/useZDRequests";
import { useZDMutations } from "~/shared/hooks/useZDMutations/useZDMutations";
import { useURLParamsGetter } from "~/hooks/useURLParamsSetter";

const ZDPage = ({ defaultEditorSelect, allowedPaths }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const goBack = () => {
    history.replace("/zd/");
  };
  const { hasPermission, loading: loadingPermissions } = usePermissions();
  const { dealerChoice } = useUser();
  const {
    selectedDate,
    currentId,
    openModal,
    fullname,
    event,
    page,
    count,
    dateBegin,
  } = useGetLocationData();
  const { PlanReportId } = useURLParamsGetter<{ PlanReportId: string }>();
  const currentDealerId = event || sessionStorage.getItem("currentDealerId");
  let language = localStorage.getItem("language");

  const isEn = language === "en";

  const isDealer =
    dealerChoice === "one" ||
    (dealerChoice === "multiple" &&
      ZD_CHECK_PERMISSIONS.every((perm) => !hasPermission(perm)));

  const setData = useSetLocationData();
  const noCaching = sessionStorage.getItem("noCaching");
  const [addNotificationMutation] = useAddNotificationMutation({});

  // Синхронизация шапки с дилером из URL (event):
  // при прямом переходе по ссылке вида /zd/main/?event=<DealerId>...
  // в шапке должен оказаться тот же дилер, что и на странице,
  // иначе формы (модалки) подставят currentDealerId из sessionStorage
  // и запрос уйдет с неправильным дилером.
  const { setDealerIdToContext } = useDealerActions();
  const { handleCurrentDealer } =
    useContext<GetCurrentDealerContextPrpos | null>(GetCurrentDealerContext) ??
    {};
  const dealerSyncQuery = useGetDealerPanelQuery({
    variables: { DealerId: event },
    skip: !event || event === sessionStorage.getItem("currentDealerId"),
    fetchPolicy: "cache-first",
  });
  useEffect(() => {
    if (!event) return;
    const dealer = get(dealerSyncQuery, "data.DealerGetter.getDealer") as
      | { id?: string; DealerName?: string; DealerNameEn?: string }
      | undefined;
    if (!dealer?.id) return;
    if (sessionStorage.getItem("currentDealerId") === dealer.id) return;
    const DealerName = dealer.DealerName ?? "";
    const DealerNameEn = dealer.DealerNameEn ?? "";
    sessionStorage.setItem("currentDealerId", dealer.id);
    sessionStorage.setItem("currentDealerName", DealerName);
    sessionStorage.setItem("currentDealerNameEn", DealerNameEn);
    setDealerIdToContext(dealer.id);
    handleCurrentDealer?.({ id: dealer.id, DealerName, DealerNameEn });
  }, [dealerSyncQuery.data, event]);

  const currentDate = selectedDate ? new Date(selectedDate) : new Date();

  const { control, watch } = useForm({
    defaultValues: {
      editorSelect: defaultEditorSelect!,
    },
  });

  const places = useGetAllPlacesQuery({
    fetchPolicy: "no-cache",
  });

  const selectPlaces = get(places, "data.PlacesGetter.getAll", []).map(
    ({ id, Name, NameEn, UUID }) => ({
      parrentNewId: id,
      value: UUID,
      label: isEn ? NameEn : Name,
      Name: isEn ? NameEn : Name,
      UUID,
      ZD_PlacesId: undefined,
    })
  );

  const placeLoading = places.loading;

  const plantReport = useGetPlanReportQuery({
    variables: {
      PlanReportId:
        PlanReportId && openModal === "Comment" ? PlanReportId : undefined,
      DealerId: currentDealerId || undefined,
    },
    fetchPolicy: "no-cache",
    skip: isDealer,
  });

  const projectQuery = useGetProjectDocumentationDealerQuery({
    variables: {
      DealerId: currentDealerId,
    },
    skip: hasPermission("dealergraph.projectDocumentation.get"),
    fetchPolicy: "no-cache",
  });

  const project = get(
    projectQuery,
    "data.DealerGetter.getProjectDocumentationDealer",
    {}
  );

  const arrIncludeProject = [
    "places",
    "warehouses",
    "placePosts",
    "tradePremises",
  ];

  const commentsPlanReport =
    openModal === "Comment" &&
    get(plantReport, "data.PlanReportGetter.getPlanReportRows.items", [])[0];

  const selectedServiceValue = watch("editorSelect")?.value ?? "services";
  const [selectedService, setSelectedService] = useState(
    defaultEditorSelect?.value ?? "services"
  );

  const requests = useZdRequests({
    currentDealerId,
    page,
    count,
    dateBegin,
    selectedService,
  });

  const options = SUB_OPTIONS[selectedService] ?? SUB_OPTIONS.services;

  useEffect(() => {
    setData("page", 0, "selected", selectedService);
  }, [selectedService]);

  const defaultStateVal = {
    loading: false,
    items: [],
    total: 0,
    refetch: () => {},
  };
  const [actualData, setActualData] = useState<any>(defaultStateVal);
  const { loading, items, refetch, total } = actualData;

  useEffect(() => {
    if (requests) {
      setActualData((p) => ({ ...p, ...requests }));
    }
  }, [requests]);

  useEffect(() => {
    setActualData(defaultStateVal);
    setSelectedService(selectedServiceValue);
  }, [selectedServiceValue]);

  const {
    change,
    deleteMut,
    hashReport,
    placeReport = undefined,
    reportPlace = undefined,
  } = useZDMutations({ selectedService });

  const zdPlanReportQuery = useGetZdDealerOneQuery({
    variables: {
      DealerId: currentDealerId,
    },
    fetchPolicy: "no-cache",
    skip: !isDealer,
  });
  const zdPlanReport = isDealer ? zdPlanReportQuery.data : {};
  const zdPlanReportRefetch = isDealer
    ? zdPlanReportQuery.refetch
    : new Function();

  const statusQuery = useGetZdDealerOneQuery({
    variables: {
      DealerId: currentDealerId,
    },
    fetchPolicy: "no-cache",
  });

  const statusDealerOne = get(
    statusQuery,
    "data.ZdCommonGetter.getZdGlobalInfoForDealer.status",
    ""
  );
  const statusDealerOnePremises = get(
    statusQuery,
    "data.ZdCommonGetter.getZdGlobalInfoForDealer.statusPremises",
    ""
  );

  const [arrStatusMutation] = useSetChangeStatusZdGlobalForDealerMutation({
    onCompleted() {
      setData("openModal", "");
      refetch();
      statusQuery.refetch();
    },
    onError({ graphQLErrors }) {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.error,
          content: graphQLErrors[0].message,
        },
      });
    },
  });

  const allZd =
    isDealer &&
    get(zdPlanReport, "data.ZdCommonGetter.getZdGlobalInfoForDealer", {});

  const checkRequire = () => {
    arrStatusMutation({
      variables: {
        DealerId: currentDealerId,
        status: ZdGlobalInfoForDealerStatus.Processing,
        isPremises: false,
      },
    });
  };
  return (
    <>
      <div className={s.wrapper}>
        <div className={s.s_dealer_title}>
          <Button
            txtBtn={""}
            onlyIcon
            onClick={goBack}
            customClassName={s.iconBack}
          >
            <Back />
          </Button>
          {t("zd.zd_title")}
          <div>
            {hasPermission("zd.submitQuestionnaireForReview") &&
              statusDealerOne !== "confirmed" && (
                <Button
                  txtBtn={t("zd.application_review")}
                  onClick={checkRequire}
                  orange
                  small
                  loading={false}
                  // loading={requireDealerChecksLoading.loading}
                  disabled={Object.keys(allZd).length > 0}
                />
              )}
            {arrIncludeProject.includes(selectedService) &&
              hasPermission("dealergraph.projectDocumentation.get") &&
              project?.File?.link && (
                <a href={project?.File?.link} target={"_blank"}>
                  <Button txtBtn={"Проектная документация"} orange small />
                </a>
              )}
          </div>
        </div>

        {dealerChoice !== "one" && (
          <DealerInfoPanel type={"ZD"} width={550} defaultDate={currentDate} />
        )}

        <Select
          name="editorSelect"
          type="editorZD.forSelect"
          control={control}
          // options={filterPathForRoles(role)}
          options={allowedPaths}
          vertical
          customClassName={clsx(s.selectEditor, {
            [s.selectEditorDealer]: dealerChoice === "one",
          })}
        />

        <TableReducerZD
          {...{
            selectedService,
            options,
            items,
            statusDealerOne,
            statusDealerOnePremises,
            loading,
            statusQuery,
            placeLoading,
            total,
            refetch,
            commentsPlanReport,
            change,
            deleteMut,
            hashReport,
            selectPlaces,
            reportPlace,
            placeReport,
          }}
        />
      </div>
    </>
  );
};
export default ZDPage;
